import type { Language } from '@astral/shared';

const LOCALE: Record<Language, string> = {
  en: 'en-US',
  fa: 'fa-IR',
  ru: 'ru-RU',
};

/**
 * Format an ISO date as a short relative string ("2h ago", "just now") using
 * the platform Intl.RelativeTimeFormat, localized to the active language.
 */
export function formatRelative(iso: string, language: Language): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSeconds = Math.round((then - now) / 1000);

  const rtf = new Intl.RelativeTimeFormat(LOCALE[language], { numeric: 'auto' });
  const divisions: Array<{ amount: number; unit: Intl.RelativeTimeFormatUnit }> = [
    { amount: 60, unit: 'second' },
    { amount: 60, unit: 'minute' },
    { amount: 24, unit: 'hour' },
    { amount: 7, unit: 'day' },
    { amount: 4.34524, unit: 'week' },
    { amount: 12, unit: 'month' },
    { amount: Number.POSITIVE_INFINITY, unit: 'year' },
  ];

  let duration = diffSeconds;
  for (const division of divisions) {
    if (Math.abs(duration) < division.amount) {
      return rtf.format(Math.round(duration), division.unit);
    }
    duration /= division.amount;
  }
  return rtf.format(Math.round(duration), 'year');
}

/** Format an ISO date as a localized short date (e.g. "Jan 5, 2026"). */
export function formatDate(iso: string, language: Language): string {
  return new Intl.DateTimeFormat(LOCALE[language], { dateStyle: 'medium' }).format(new Date(iso));
}
